import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Avatar, Box, Divider, Group, Stack, Tooltip } from '@mantine/core'
import { AppSkeleton } from '../../app/common/ui'
import { toast } from 'react-toastify'
import {
  IoPeopleOutline,
  IoChatbubbleOutline,
  IoThumbsUp,
  IoThumbsUpOutline,
  IoTrashOutline,
  IoSettingsOutline,
  IoLockClosedOutline,
  IoArrowBack
} from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { CacheDetailDto, CacheGunDto, CacheMemberDto, CachePostDto } from '../../app/models/cache'
import type { PollListItemDto } from '../../app/models/poll'
import { AppButton, AppCard, AppText, AmmoScoreBadge, EmptyState, ImageLightbox, PostTypeBadge } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { uiTokens } from '../../app/theme/tokens'
import { CacheCover, CacheVisibilityBadge, SectionLabel, StatChip, resolveCacheCover, timeAgo } from './cacheUi'
import CacheSettingsModal from './CacheSettingsModal'
import CachePostForm from './CachePostForm'

const PAGE_MAX = 920

function MemberAvatar ({ member }: { member: CacheMemberDto }) {
  const role = member.isCreator ? 'Creator' : member.isModerator ? 'Deputy' : 'Member'
  const roleRing = member.isCreator ? 'var(--gs-accent)' : member.isModerator ? 'var(--gs-blue)' : 'transparent'
  return (
    <Tooltip label={`@${member.username ?? 'user'} · ${role}`} withArrow>
      <Box
        component={Link}
        to={`/profiles/${member.username}`}
        sx={{
          textDecoration: 'none',
          display: 'block',
          transition: 'transform 120ms ease',
          '&:hover': { transform: 'translateY(-3px)', zIndex: 2 }
        }}
      >
        <Avatar
          radius="xl"
          size={38}
          color={member.isCreator ? 'ember' : 'gray'}
          variant="filled"
          sx={{
            border: '2px solid var(--gs-surface)',
            boxShadow: roleRing === 'transparent' ? 'none' : `0 0 0 2px ${roleRing}`,
            fontWeight: 600,
            fontSize: 13
          }}
        >
          {(member.username ?? '?').slice(0, 2).toUpperCase()}
        </Avatar>
      </Box>
    </Tooltip>
  )
}

function GunRow ({ gun }: { gun: CacheGunDto }) {
  const to = gun.firearmId ? `/gun-vault/${gun.firearmId}` : gun.firearmModelId ? `/registry/${gun.firearmModelId}` : null
  return (
    <Group spacing="xs" position="apart" noWrap sx={{ padding: '6px 0' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {to
          ? (
          <AppText component={Link} to={to} size="sm" weight={500} sx={{ color: 'var(--gs-accent)', textDecoration: 'none' }} lineClamp={1}>
            {gun.firearmLabel ?? 'Unnamed'}
          </AppText>
            )
          : <AppText size="sm" weight={500} lineClamp={1}>{gun.firearmLabel ?? '—'}</AppText>}
        {gun.note && <AppText size="xs" color="dimmed" lineClamp={1}>{gun.note}</AppText>}
      </Box>
      <AppText size="xs" color="dimmed" sx={{ flexShrink: 0 }}>@{gun.username}</AppText>
    </Group>
  )
}

export default observer(function CacheDetailPage () {
  const { id } = useParams<{ id: string }>()
  const { userStore } = useStore()
  const [cache, setCache] = useState<CacheDetailDto | null>(null)
  const [posts, setPosts] = useState<CachePostDto[]>([])
  const [loading, setLoading] = useState(true)
  const [showComposer, setShowComposer] = useState(false)
  const [joining, setJoining] = useState(false)
  const [votingId, setVotingId] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [postsPage, setPostsPage] = useState(1)
  const [postsTotal, setPostsTotal] = useState(0)
  const [loadingMorePosts, setLoadingMorePosts] = useState(false)
  const [polls, setPolls] = useState<PollListItemDto[]>([])
  const [pollsPage, setPollsPage] = useState(1)
  const [pollsTotal, setPollsTotal] = useState(0)
  const [loadingMorePolls, setLoadingMorePolls] = useState(false)
  const POSTS_PAGE_SIZE = 20
  const POLLS_PAGE_SIZE = 10

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setPostsPage(1)
    setPollsPage(1)
    try {
      const c = await agent.Caches.get(id)
      setCache(c)
      if (c.canViewBoard) {
        const p = await agent.Caches.listPosts(id, 1, POSTS_PAGE_SIZE)
        setPosts(p.items)
        setPostsTotal(p.totalCount)
        const pollsResult = await agent.Polls.listByCache(id, { pageNumber: 1, pageSize: POLLS_PAGE_SIZE })
        setPolls(pollsResult.items)
        setPollsTotal(pollsResult.totalCount)
      } else {
        setPosts([])
        setPostsTotal(0)
        setPolls([])
        setPollsTotal(0)
      }
    } catch {
      setCache(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  const loadMorePosts = async () => {
    if (!id) return
    const next = postsPage + 1
    setLoadingMorePosts(true)
    try {
      const p = await agent.Caches.listPosts(id, next, POSTS_PAGE_SIZE)
      setPosts((prev) => [...prev, ...p.items])
      setPostsTotal(p.totalCount)
      setPostsPage(next)
    } catch {
      /* interceptor */
    } finally {
      setLoadingMorePosts(false)
    }
  }

  const loadMorePolls = async () => {
    if (!id) return
    const next = pollsPage + 1
    setLoadingMorePolls(true)
    try {
      const p = await agent.Polls.listByCache(id, { pageNumber: next, pageSize: POLLS_PAGE_SIZE })
      setPolls((prev) => [...prev, ...p.items])
      setPollsTotal(p.totalCount)
      setPollsPage(next)
    } catch {
      /* interceptor */
    } finally {
      setLoadingMorePolls(false)
    }
  }

  useEffect(() => {
    void load()
  }, [load])

  const toggleJoin = async () => {
    if (!cache || !id) return
    setJoining(true)
    try {
      if (cache.isMember) {
        await agent.Caches.leave(id)
        toast.success('Left cache')
      } else {
        await agent.Caches.join(id)
        toast.success('Joined cache')
      }
      await load()
    } catch {
      /* interceptor */
    } finally {
      setJoining(false)
    }
  }

  const handleVote = async (postId: string) => {
    if (!id || !userStore.isLoggedIn) return
    setVotingId(postId)
    try {
      const updated = await agent.Caches.voteOnPost(id, postId)
      setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, voteScore: updated.voteScore, currentUserVote: updated.currentUserVote } : p))
    } catch {
      /* interceptor */
    } finally {
      setVotingId(null)
    }
  }

  const handleDeletePost = async (postId: string) => {
    if (!id) return
    try {
      await agent.Caches.deletePost(id, postId)
      setPosts((prev) => prev.filter((p) => p.id !== postId))
      setPostsTotal((prev) => prev - 1)
      toast.success('Post deleted')
    } catch {
      /* interceptor */
    }
  }

  if (loading) {
    return (
      <Box sx={{ padding: '20px 24px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <AppSkeleton radius={12} mb="md" sx={{ aspectRatio: '16 / 7', width: '100%' }} />
        <AppSkeleton height={24} width="40%" mb="sm" />
        <AppSkeleton height={14} width="70%" mb="lg" />
        <AppSkeleton height={120} radius={12} />
      </Box>
    )
  }

  if (!cache) {
    return (
      <Box sx={{ padding: '24px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <AppButton component={Link} to="/caches" variant="subtle" size="xs" mb="md" leftIcon={<IoArrowBack size={13} />}>Caches</AppButton>
        <EmptyState title="Cache not found" message="This cache may be private or no longer exists." />
      </Box>
    )
  }

  const currentUsername = userStore.user?.username

  return (
    <Box sx={{ padding: '16px 24px 48px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
      <AppButton component={Link} to="/caches" variant="subtle" size="xs" mb="sm" leftIcon={<IoArrowBack size={13} />}>Caches</AppButton>

      {/* Cover banner with overlaid identity */}
      <CacheCover
        url={resolveCacheCover(cache)}
        name={cache.name}
        seed={cache.id}
        aspectRatio="16 / 7"
        onImageClick={setLightboxUrl}
        overlay={
          <Box>
            <Group spacing="xs" mb={6}>
              {cache.isOfficial && (
                <Box sx={{ background: 'rgba(0,0,0,0.5)', borderRadius: 6, padding: '2px 8px', backdropFilter: 'blur(4px)' }}>
                  <AppText size="xs" weight={700} sx={{ color: '#ffce6b', textTransform: 'uppercase', letterSpacing: 0.6 }}>Official</AppText>
                </Box>
              )}
              <CacheVisibilityBadge visibility={cache.visibility} size="sm" hidePublic />
            </Group>
            <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 32, color: '#fff', lineHeight: 1.05, textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}>
              {cache.name}
            </AppText>
            {cache.firearmModelMake && (
              <AppText size="sm" weight={500} sx={{ color: 'rgba(255,255,255,0.88)', textShadow: '0 1px 6px rgba(0,0,0,0.7)' }}>
                {cache.firearmModelMake} {cache.firearmModelName}
              </AppText>
            )}
          </Box>
        }
      />

      {/* Action + stats bar */}
      <Group position="apart" align="center" mt="md" mb="lg" sx={{ flexWrap: 'wrap', gap: 12 }}>
        <Group spacing="lg">
          <StatChip icon={<IoPeopleOutline size={15} />}>{cache.memberCount} member{cache.memberCount === 1 ? '' : 's'}</StatChip>
          <StatChip icon={<IoChatbubbleOutline size={15} />}>{cache.postCount} post{cache.postCount === 1 ? '' : 's'}</StatChip>
          {cache.creatorUsername && (
            <AppText size="sm" color="dimmed">
              by <AppText component={Link} to={`/profiles/${cache.creatorUsername}`} span sx={{ color: 'var(--gs-accent)', textDecoration: 'none', fontWeight: 500 }}>@{cache.creatorUsername}</AppText>
            </AppText>
          )}
        </Group>
        <Group spacing="xs">
          {cache.firearmModelId && (
            <AppButton component={Link} to={`/registry/${cache.firearmModelId}`} variant="default" size="sm">
              View in Registry
            </AppButton>
          )}
          {cache.canManage && (
            <AppButton variant="default" size="sm" leftIcon={<IoSettingsOutline size={15} />} onClick={() => setSettingsOpen(true)}>
              Settings
            </AppButton>
          )}
          {userStore.isLoggedIn && (
            <AppButton
              size="sm"
              variant={cache.isMember ? 'outline' : 'filled'}
              color="ember"
              loading={joining}
              onClick={() => void toggleJoin()}
            >
              {cache.isMember ? 'Leave' : 'Join'}
            </AppButton>
          )}
        </Group>
      </Group>

      {cache.isArchived && (
        <AppCard mb="lg" sx={{ background: 'var(--gs-surface2)', borderColor: 'var(--gs-gold)' }}>
          <AppText size="sm" weight={600}>This cache is archived</AppText>
          <AppText size="xs" color="dimmed">It is hidden from browse and search. Reopen it from Settings.</AppText>
        </AppCard>
      )}

      {cache.description && <AppText size="sm" mb="lg" sx={{ whiteSpace: 'pre-wrap' }}>{cache.description}</AppText>}

      {/* Private gate for non-members */}
      {!cache.canViewBoard
        ? (
        <AppCard sx={{ textAlign: 'center', padding: 32 }}>
          <Group position="center" mb="sm"><IoLockClosedOutline size={28} color="var(--gs-muted)" /></Group>
          <AppText weight={600} mb={4}>This is a private cache</AppText>
          <AppText size="sm" color="dimmed" mb="md">
            The discussion board and roster are members-only. Join to take part — anyone can.
          </AppText>
          {userStore.isLoggedIn
            ? <AppButton color="ember" loading={joining} onClick={() => void toggleJoin()}>Join cache</AppButton>
            : <AppButton component={Link} to="/login" color="ember">Log in to join</AppButton>}
        </AppCard>
          )
        : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 300px',
            gap: 24,
            alignItems: 'start',
            '@media (max-width: 768px)': { gridTemplateColumns: '1fr' }
          }}
        >
          {/* Main column: composer + discussion */}
          <Stack spacing="md">
            {cache.isMember && cache.canPost && (
              <>
                {!showComposer
                  ? (
                  <AppCard
                    sx={{ background: 'var(--gs-surface)', cursor: 'pointer', '&:hover': { borderColor: 'var(--gs-border-bright)' } }}
                    onClick={() => setShowComposer(true)}
                  >
                    <AppText size="sm" color="dimmed">What's on your mind? Start a post…</AppText>
                  </AppCard>
                    )
                  : (
                  <AppCard sx={{ background: 'var(--gs-surface)' }}>
                    <AppText size="sm" weight={600} mb="sm">New post</AppText>
                    <CachePostForm
                      cacheId={id!}
                      onSuccess={(post) => {
                        setPosts((prev) => [post, ...prev])
                        setPostsTotal((prev) => prev + 1)
                        setShowComposer(false)
                      }}
                      onCancel={() => setShowComposer(false)}
                    />
                  </AppCard>
                    )}
              </>
            )}

            {cache.isMember && !cache.canPost && (
              <AppCard sx={{ background: 'var(--gs-surface2)' }}>
                <AppText size="xs" color="dimmed">
                  You need a higher AmmoScore to post here, but you can read and vote.
                </AppText>
              </AppCard>
            )}

            <SectionLabel>{postsTotal > 0 ? `Discussion · ${postsTotal}` : 'Discussion'}</SectionLabel>

            {posts.length === 0
              ? (
              <EmptyState
                title="No posts yet"
                message={cache.isMember && cache.canPost ? 'Be the first to start a thread.' : 'Check back soon.'}
              />
                )
              : (
              <Stack spacing="sm">
                {posts.map((p) => {
                  const isPostAuthor = currentUsername != null && p.authorUsername === currentUsername
                  const canDelete = isPostAuthor || cache.isModerator
                  const hasVoted = (p.currentUserVote ?? 0) > 0
                  return (
                    <AppCard
                      key={p.id}
                      padding="sm"
                      sx={{
                        background: 'var(--gs-surface)',
                        transition: 'border-color 150ms ease, box-shadow 150ms ease',
                        '&:hover': { borderColor: 'var(--gs-border-bright)', boxShadow: uiTokens.elevation.sm }
                      }}
                    >
                      <Group position="apart" align="flex-start" noWrap>
                        {/* Clickable content */}
                        <Box
                          component={Link}
                          to={`/caches/${id}/posts/${p.id}`}
                          sx={{ flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}
                        >
                          <Group spacing={6} mb={4} noWrap align="center">
                            <PostTypeBadge type={p.postType as any} />
                          </Group>
                          <AppText weight={600} size="sm" lineClamp={1}>{p.title}</AppText>
                          {p.body && (
                            <AppText size="xs" color="dimmed" lineClamp={1} mt={2}>{p.body}</AppText>
                          )}
                          <Group spacing={6} mt={6} align="center" noWrap sx={{ flexWrap: 'wrap' }}>
                            <Avatar
                              size={16}
                              radius="xl"
                              src={p.authorImage || undefined}
                              color="ember"
                              sx={{ flexShrink: 0, fontWeight: 700, fontSize: 9 }}
                            >
                              {(p.authorDisplayName || p.authorUsername || '?').slice(0, 1).toUpperCase()}
                            </Avatar>
                            <AppText size="xs" weight={500}>{p.authorDisplayName || p.authorUsername}</AppText>
                            {p.authorAmmoTier && (
                              <AmmoScoreBadge tier={p.authorAmmoTier} score={p.authorAmmoScore} size="sm" />
                            )}
                            <AppText size="xs" color="dimmed">
                              · {p.commentCount} comment{p.commentCount !== 1 ? 's' : ''} · {timeAgo(p.createdAt)}
                            </AppText>
                          </Group>
                        </Box>

                        {/* Right: thumbnail + actions */}
                        <Stack spacing={6} align="flex-end" sx={{ flexShrink: 0, paddingLeft: 10 }}>
                          {p.coverImageUrl && (
                            <Box
                              component={Link}
                              to={`/caches/${id}/posts/${p.id}`}
                              sx={{ width: 56, height: 42, borderRadius: 5, overflow: 'hidden', flexShrink: 0, display: 'block' }}
                            >
                              <img
                                src={p.coverImageUrl}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                alt=""
                              />
                            </Box>
                          )}
                          <Group spacing={4} align="center">
                            {userStore.isLoggedIn
                              ? (
                              <Tooltip label={hasVoted ? 'Remove vote' : 'Upvote'} withArrow>
                                <AppButton
                                  size="xs"
                                  variant={hasVoted ? 'filled' : 'subtle'}
                                  color={hasVoted ? 'ember' : 'gray'}
                                  leftIcon={hasVoted ? <IoThumbsUp size={12} /> : <IoThumbsUpOutline size={12} />}
                                  loading={votingId === p.id}
                                  onClick={(e: React.MouseEvent) => { e.preventDefault(); void handleVote(p.id) }}
                                  compact
                                >
                                  {p.voteScore}
                                </AppButton>
                              </Tooltip>
                                )
                              : p.voteScore > 0
                                ? <AppText size="xs" color="dimmed">▲ {p.voteScore}</AppText>
                                : null}
                            {canDelete && (
                              <AppButton
                                size="xs"
                                variant="subtle"
                                color="red"
                                aria-label="Delete post"
                                onClick={(e: React.MouseEvent) => { e.preventDefault(); void handleDeletePost(p.id) }}
                                compact
                              >
                                <IoTrashOutline size={13} />
                              </AppButton>
                            )}
                          </Group>
                        </Stack>
                      </Group>
                    </AppCard>
                  )
                })}
              </Stack>
                )}

            {posts.length < postsTotal && (
              <Group position="center" mt="xs">
                <AppButton variant="subtle" size="sm" loading={loadingMorePosts} onClick={() => void loadMorePosts()}>
                  Load more posts
                </AppButton>
              </Group>
            )}

            {/* Polls section */}
            {(cache.isMember && cache.canPost) || pollsTotal > 0
              ? (
              <>
                <Divider my="lg" />
                <Group position="apart" align="center" mb="md">
                  <SectionLabel>{pollsTotal > 0 ? `Polls · ${pollsTotal}` : 'Polls'}</SectionLabel>
                  {userStore.isLoggedIn && cache.isMember && cache.canPost && (
                    <AppButton
                      component={Link}
                      to={`/polls/new?cacheId=${id}`}
                      size="xs"
                      variant="outline"
                    >
                      Create poll
                    </AppButton>
                  )}
                </Group>

                {polls.length === 0
                  ? (
                  <EmptyState
                    title="No polls yet"
                    message={cache.canPost ? 'Be the first to start a poll.' : 'Check back soon.'}
                  />
                    )
                  : (
                  <Stack spacing="sm">
                    {polls.map((poll) => (
                      <AppCard
                        key={poll.id}
                        padding="sm"
                        component={Link}
                        to={`/polls/${poll.id}`}
                        sx={{
                          background: 'var(--gs-surface)',
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: 'border-color 150ms ease, box-shadow 150ms ease',
                          '&:hover': { borderColor: 'var(--gs-border-bright)', boxShadow: uiTokens.elevation.sm }
                        }}
                      >
                        <AppText weight={600} size="sm" lineClamp={1}>{poll.question}</AppText>
                        <Group spacing={6} mt={4}>
                          <AppText size="xs" color="dimmed">@{poll.authorUsername}</AppText>
                          <AppText size="xs" color="dimmed">· {poll.optionCount} option{poll.optionCount !== 1 ? 's' : ''}</AppText>
                          <AppText size="xs" color="dimmed">· {timeAgo(poll.createdAt)}</AppText>
                        </Group>
                      </AppCard>
                    ))}
                  </Stack>
                    )}

                {polls.length < pollsTotal && (
                  <Group position="center" mt="xs">
                    <AppButton variant="subtle" size="sm" loading={loadingMorePolls} onClick={() => void loadMorePolls()}>
                      Load more polls
                    </AppButton>
                  </Group>
                )}
              </>
                )
              : null}
          </Stack>

          {/* Right rail: roster */}
          <Stack spacing="lg">
            {cache.members.length > 0 && (
              <AppCard>
                <Group position="apart" mb="sm">
                  <SectionLabel>Members</SectionLabel>
                  <AppText size="xs" color="dimmed">{cache.memberCount}</AppText>
                </Group>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 8, '& > *:not(:first-of-type)': { marginLeft: -10 } }}>
                  {cache.members.slice(0, 16).map((m) => <MemberAvatar key={m.userId} member={m} />)}
                  {cache.memberCount > 16 && (
                    <Avatar radius="xl" size={38} color="dark" sx={{ border: '2px solid var(--gs-surface)', fontSize: 12, fontWeight: 600 }}>
                      +{cache.memberCount - 16}
                    </Avatar>
                  )}
                </Box>
              </AppCard>
            )}

            {cache.guns.length > 0 && (
              <AppCard>
                <SectionLabel>Guns in this cache</SectionLabel>
                <Divider my={8} />
                <Stack spacing={0}>
                  {cache.guns.slice(0, 10).map((g) => <GunRow key={g.id} gun={g} />)}
                </Stack>
              </AppCard>
            )}
          </Stack>
        </Box>
          )}

      {cache.canManage && (
        <CacheSettingsModal
          opened={settingsOpen}
          cache={cache}
          onClose={() => setSettingsOpen(false)}
          onUpdated={(updated) => { setCache(updated); void load() }}
        />
      )}

      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} alt={cache.name} />
    </Box>
  )
})
